import { useState } from 'react';
import { startAuthentication, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import toast from 'react-hot-toast';

import { Modal } from './ui/Modal.jsx';
import { Input } from './ui/Input.jsx';
import { Button } from './ui/Button.jsx';
import { mfaApi } from '../api/mfa.js';
import { passkeysApi } from '../api/passkeys.js';
import { ApiError } from '../api/client.js';
import { t } from '../lib/i18n.js';

/**
 * Step up MFA prompt.
 *
 * Pattern: a destructive call returns 403 MFA_REQUIRED. Catch it,
 * open this modal, on success retry the original action. The modal
 * owns both methods (TOTP and passkey) and lets the user choose
 * which one to use; the server accepts either to refresh
 * `Session.mfaVerifiedAt`.
 */
export function MfaPrompt({ open, onClose, onVerified }) {
  const [mode, setMode] = useState('totp');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => { setCode(''); setError(null); setSubmitting(false); setMode('totp'); };
  const handleClose = () => { if (!submitting) { reset(); onClose(); } };

  const submitTotp = async (e) => {
    e.preventDefault();
    if (!code || code.length < 6) {
      setError(t('mfa.error.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await mfaApi.challenge(code.trim());
      reset();
      onVerified();
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'MFA_INVALID' || err.code === 'MFA_REQUIRED')) {
        setError(t('mfa.error.invalid'));
      } else {
        toast.error(err.message || t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPasskey = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const options = await passkeysApi.stepupOptions();
      const response = await startAuthentication({ optionsJSON: options });
      await passkeysApi.stepupVerify(response);
      reset();
      onVerified();
    } catch (err) {
      if (err && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
        setError('Operazione annullata.');
      } else if (err instanceof ApiError && (err.code === 'MFA_INVALID' || err.code === 'BAD_REQUEST')) {
        setError('Passkey non valida o scaduta.');
      } else {
        toast.error(err.message || t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t('mfa.title')}
      description="Per procedere serve una verifica recente. Scegli il metodo che preferisci."
      size="sm"
    >
      <div className="mb-4 inline-flex rounded-md border border-border bg-surface p-0.5">
        <button
          type="button"
          className={`rounded px-3 py-1.5 text-xs font-medium ${mode === 'totp' ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}
          onClick={() => { setMode('totp'); setError(null); }}
        >
          Codice (app)
        </button>
        <button
          type="button"
          className={`rounded px-3 py-1.5 text-xs font-medium ${mode === 'passkey' ? 'bg-ink text-white' : 'text-ink-muted hover:text-ink'}`}
          onClick={() => { setMode('passkey'); setError(null); }}
          disabled={!browserSupportsWebAuthn()}
        >
          Passkey
        </button>
      </div>

      {mode === 'totp' ? (
        <form className="flex flex-col gap-4" onSubmit={submitTotp} noValidate>
          <Input
            label={t('mfa.code')}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            maxLength={8}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={error}
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={handleClose} disabled={submitting}>{t('common.cancel')}</Button>
            <Button type="submit" loading={submitting}>{submitting ? t('mfa.submitting') : t('mfa.submit')}</Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-muted">
            Conferma con la biometria, il PIN del dispositivo o la chiave hardware.
          </p>
          {error && <p className="text-xs text-danger">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" type="button" onClick={handleClose} disabled={submitting}>{t('common.cancel')}</Button>
            <Button onClick={submitPasskey} loading={submitting}>Verifica con passkey</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
